import type { Editor } from "@tiptap/react";
import type Quill from "quill";
import type { RangeStatic } from "quill";
import type { DictationTarget } from "shared";
import { insertDictatedText } from "./insertDictatedText";

/** A TipTap editor as a dictation target — null once it's gone. */
export const tiptapTarget = (
  editor: Editor | null | undefined,
): DictationTarget | null =>
  editor && !editor.isDestroyed
    ? { insert: (text) => insertDictatedText(editor, text) }
    : null;

/**
 * Same rule as TipTap: a space unless the caret starts a line or follows
 * whitespace. Quill reads an embed (chip, image) as "", which gets one.
 */
const needsLeadingSpace = (quill: Quill, index: number): boolean => {
  if (index === 0) return false;
  const before = quill.getText(index - 1, 1);
  return before === "" || !/\s/.test(before);
};

/**
 * Inserts at the caret the editor had before the mic was pressed, replacing a
 * selection as TipTap does. `getSelection(true)` focuses the editor and
 * restores that caret, so the editor must be visible by now.
 */
const insertIntoQuill = (quill: Quill, text: string): void => {
  const range: RangeStatic | null = quill.getSelection(true);
  // Quill's text always ends in "\n" — the end of the note is just before it.
  const index = range?.index ?? quill.getLength() - 1;
  if (range && range.length > 0) {
    quill.deleteText(range.index, range.length, "user");
  }
  const words = needsLeadingSpace(quill, index) ? ` ${text}` : text;
  quill.insertText(index, words, "user");
  quill.setSelection(index + words.length, 0, "user");
};

/** A Quill editor as a dictation target — null once it's gone. */
export const quillTarget = (
  quill: Quill | null | undefined,
): DictationTarget | null =>
  quill ? { insert: (text) => insertIntoQuill(quill, text) } : null;
